import type { CreateCatalogEntryPayload } from '@/api/productsApi'

/**
 * Splitting a catalog import into request-sized batches.
 *
 * The ISBT 128 file is 14,911 rows. Sent in one POST that is ~15 MB of JSON,
 * which the API refuses: no body-parser limit is configured on the backend, so
 * Express's 100 kb default applies and the import 413s before a single row
 * lands. Batching keeps every request comfortably inside that, and gives the
 * dialog something honest to show for an import that takes a while.
 *
 * Pure and dependency-free so it can be unit-tested — `vitest.config.ts` only
 * matches `src/**\/*.test.ts`.
 */

/**
 * Byte budget for one request body.
 *
 * 60 KB against a 100 kb ceiling. The margin is deliberate: `JSON.stringify`
 * here counts UTF-16 code units while the limit is applied to UTF-8 bytes, and
 * a multi-byte character would make this measurement optimistic.
 */
export const IMPORT_BATCH_BYTES = 60_000

/**
 * Drop the columns the file left blank.
 *
 * Every parsed column is optional server-side and defaults to `''`, so an
 * omitted key and an empty one persist identically — and omitting them is what
 * takes the payload from ~1,018 to ~374 bytes a row. The code is never dropped:
 * it is the key the import upserts on.
 */
export function stripEmpty(entry: CreateCatalogEntryPayload): CreateCatalogEntryPayload {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(entry)) {
    if (key === 'productDescriptionCode' || value !== '') out[key] = value
  }
  return out as CreateCatalogEntryPayload
}

/**
 * Split entries into batches no larger than `maxBytes` of serialized JSON.
 *
 * Measured rather than a fixed row count: rows vary from a bare code to all 44
 * columns filled, so a count that is safe for one file can be over the limit for
 * the next. A single row larger than the budget still gets its own batch — it
 * would fail either way, and dropping it silently would be worse.
 */
export function batchBySize<T>(entries: T[], maxBytes = IMPORT_BATCH_BYTES): T[][] {
  const batches: T[][] = []
  let current: T[] = []
  // Tracks the serialized length of `current` so each row is stringified once
  // instead of re-serializing the whole batch on every addition.
  let size = 0

  for (const entry of entries) {
    const entrySize = JSON.stringify(entry).length + 1 // +1 for the separator
    if (current.length > 0 && size + entrySize > maxBytes) {
      batches.push(current)
      current = []
      size = 0
    }
    current.push(entry)
    size += entrySize
  }
  if (current.length > 0) batches.push(current)
  return batches
}

/** Running totals while an import is in flight. */
export type ImportProgress = { done: number; total: number }
